// Minimal, dependency-free JWT claim reader for the session manager.
//
// The client never verifies the signature (only the server can) — it just needs
// exp and login_at to schedule refreshes and enforce the absolute ceiling ahead
// of the server. Decoding is best-effort: a malformed token yields nulls and the
// manager falls back to conservative behaviour.

export interface EdgeSessionClaims {
  /** Access-token expiry, in ms since epoch (from the `exp` claim, seconds). */
  expMs: number | null;
  /** Login time, in ms since epoch (from the `login_at` claim, seconds). */
  loginAtMs: number | null;
  sessionId: string | null;
}

function base64UrlDecode(input: string): string | null {
  try {
    const padded = input.replace(/-/g, "+").replace(/_/g, "/");
    const withPad = padded + "===".slice((padded.length + 3) % 4);
    // atob is available in all supported browsers.
    return atob(withPad);
  } catch {
    return null;
  }
}

export function decodeEdgeSessionClaims(
  token: string | null | undefined,
): EdgeSessionClaims {
  const empty: EdgeSessionClaims = {
    expMs: null,
    loginAtMs: null,
    sessionId: null,
  };
  if (!token) return empty;

  const parts = token.split(".");
  if (parts.length < 2) return empty;

  const json = base64UrlDecode(parts[1]);
  if (!json) return empty;

  try {
    const claims = JSON.parse(json) as Record<string, unknown>;
    const exp = typeof claims.exp === "number" ? claims.exp : null;
    const loginAt =
      typeof claims.login_at === "number" ? claims.login_at : null;
    const sessionId =
      typeof claims.session_id === "string" ? claims.session_id : null;
    return {
      expMs: exp !== null ? exp * 1000 : null,
      loginAtMs: loginAt !== null ? loginAt * 1000 : null,
      sessionId,
    };
  } catch {
    return empty;
  }
}

/**
 * Whether a 401 for a request sent with requestToken may end the session in use
 * now (currentToken). False only when both carry a session_id and they differ:
 * the request was made by an older session — the user has signed in again since
 * — and its late "session ended" answer must not log out the new one (#1100).
 * Anything undecidable (no request token, no session on either side) keeps the
 * previous behaviour and returns true. requestToken may carry a "Bearer" prefix.
 */
export function rejectionAppliesToCurrentSession(
  requestToken: string | null | undefined,
  currentToken: string | null | undefined,
): boolean {
  const raw =
    typeof requestToken === "string"
      ? requestToken.replace(/^\s*bearer\s+/i, "").trim()
      : "";
  const requestSid = decodeEdgeSessionClaims(raw || null).sessionId;
  const currentSid = decodeEdgeSessionClaims(currentToken).sessionId;
  return !requestSid || !currentSid || requestSid === currentSid;
}
